import { BellRingingIcon } from "@phosphor-icons/react";
import { useI18N, useFooterSettings } from "common/hooks";
import footerDataFallback from "@/components/Footer/footerData.json";
import { useNavigate } from "@tanstack/react-router";


interface NotificationEmptyStateProps {
  onManageNotifications?: () => void;
}

function NotificationEmptyState({
  onManageNotifications,
}: NotificationEmptyStateProps) {
  const i18n = useI18N();
  const { footerSettings, isLoading } = useFooterSettings();
  // Use API data if available, otherwise fall back to JSON
  const footerData = footerSettings || footerDataFallback;
  const navigate = useNavigate();

  const handleManageNotifications = () => {
    navigate({ to: "/settings" });
  };

  return (
    <div
      className="flex flex-col gap-4 items-center justify-center px-4 py-6"
      role="region"
      aria-label="Notification confirmation"
    >
      {/* BellIcon Icon */}
      <div
        className="flex items-center justify-center w-8 h-8"
        aria-hidden="true"
      >
        <BellRingingIcon
          size={32}
          weight="regular"
          className="text-fg-neutral-strong"
        />
      </div>

      {/* Text Content */}
      <div className="flex flex-col items-start w-full text-center">
        {/* Title */}
        <div className="flex flex-col justify-center w-full">
          <h2 className="title-s-semibold text-fg-neutral-strong">
            {i18n.get("youllNowGetNotificationsForThisVenue")}
          </h2>
        </div>

        {/* Subtitle */}
        <div className="flex flex-col justify-center w-full mt-1">
          <p className="label-s-regular text-fg-neutral-subtle">
            {i18n.get("pushNotificationsArentAvailableOnWebYet")}
            <br />
            {i18n.get("downloadTheAppToGetLiveAlerts")}
          </p>
        </div>
      </div>

      {/* App Download Section */}
      <div className="flex flex-col items-center justify-center w-full">
        <div className="flex gap-2.5 items-center py-3 md:gap-4">
          {/* QR Code */}
          <div
            className="relative w-[120px] h-[120px] flex-shrink-0"
            aria-label="QR code to download app"
          >
            <img
              src="/images/Sociability_App_Download_Smart_Link_QR_Code.png"
              alt="QR code to download Sociability app"
              className="w-full h-full object-cover rounded-lg"
            />
          </div>


          {/* App Store Buttons */}
          {!isLoading && (
            <div className="flex flex-col gap-4 items-start justify-center">
              {footerData.appStoreLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="focus-blue inline-flex h-10 w-[120px] shrink-0 items-center justify-center transition-opacity hover:opacity-80"
                  aria-label={i18n.t(link.ariaLabelKey)}
                >
                  <img
                    src={link.image}
                    alt={i18n.t(link.ariaLabelKey)}
                    className="h-full w-full object-contain"
                  />
                </a>
              ))}
            </div>
          )}

        </div>

        {/* Manage Notifications Link */}
        {onManageNotifications && (
          <button
            type="button"
            onClick={handleManageNotifications}
            className="flex flex-col justify-center min-h-[40px] min-w-[40px] p-2.5 rounded-lg bg-bg-brand-ghost-default hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-active transition-colors focus-blue"
            aria-label="Manage notification settings"
          >
            <span className="label-m-medium text-fg-neutral-strong underline text-center whitespace-nowrap">
              {i18n.get("manageNotifications")}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

export default NotificationEmptyState;
